import CreateButton from "../components/CreateButton"
import { normalizeOrderCoupon } from '../utils/orderCoupon'
import { hasUnchangedInstallmentAmounts, getInstallmentsTotal, getInstallmentsPaymentSummary, isLockedRefundedInstallment, isRefundedInstallment, isFixedInstallment, updateInstallmentAmounts, parseRefundAmount } from '../utils/orderInstallments'
import { addDays, addMonths, addWeeks, addYears } from "date-fns"
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import TextareaAutosize from 'react-textarea-autosize'
import JsonView from 'react18-json-view'
import 'react18-json-view/src/dark.css'
import 'react18-json-view/src/style.css'
import api from "../api"
import { ReactComponent as Amex } from "../assets/images/amex.svg"
import { ReactComponent as BankStripe } from "../assets/images/bank_stripe.svg"
import { ReactComponent as CreditCardIcon } from "../assets/images/icons/ic-card.svg"
import { ReactComponent as CopyIcon } from "../assets/images/icons/ic-copy.svg"
import { ReactComponent as CheckIcon } from "../assets/images/icons/ic-check.svg"
import { ReactComponent as MenuIcon } from "../assets/images/icons/ic-menu.svg"
import { ReactComponent as DeleteIcon } from "../assets/images/icons/ic-delete.svg"
import { ReactComponent as SendIcon } from "../assets/images/icons/ic-send.svg"
import { ReactComponent as Mastercard } from "../assets/images/mastercard.svg"
import { ReactComponent as PayPal } from "../assets/images/paypal.svg"
import UserPlaceholder from "../assets/images/placeholders/user-placeholder.png"
import { ReactComponent as Scalapay } from "../assets/images/scalapay.svg"
import { ReactComponent as Visa } from "../assets/images/visa.svg"
import { Each } from "../common/Each"
import MainContext from "../common/MainContext"
import { ButtonStatus, InstallmentType, OrderStatus } from "../common/constants"
import useWindowDimensions from "../common/hooks/useWindowDimensions"
import Button from "../components/Button"
import Breadcrumb from "../components/Breadcrumb"
import Checkbox from "../components/Checkbox"
import FrequencySelector from "../components/FrequencySelector"
import Select from "../components/Select"
import StatusBadge from "../components/StatusBadge"
import TextInput from "../components/TextInput"
import Card from "../components/cards/Card"
import AlertDialog from "../components/dialogs/AlertDialog"
import Dialog from "../components/dialogs/Dialog"
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout"
import { DialogStatus } from "../enums"
import typo from "../typography.module.css"
import { formatDateV2, formatPrice, formatTimeV2, getLogPhrase, getOrderStatusBadge, groupLogsByDate } from "../utils"
import styles from "./Order.module.css"

const toCents = (value) => {
    const parsed = Number(value)
    return Number.isNaN(parsed) ? 0 : Math.round(parsed)
}

const getInstallmentsAmountMismatch = (order) => {
    if (!order?.installments || order.installments.length === 0) return null

    const installmentsTotal = getInstallmentsTotal(order.installments)
    const orderAmount = toCents(order.amount)

    if (installmentsTotal === orderAmount) return null

    return {
        installmentsTotal,
        orderAmount,
        difference: installmentsTotal - orderAmount,
    }
}

const isInstallmentsTotalMatchingOrder = (order) => {
    return getInstallmentsAmountMismatch(order) === null
}

const GIFT_TEMPLATE_LABELS = {
    birthday: 'Compleanno',
    graduation: 'Laurea',
    christmas: 'Natale e festività',
    special_occasion: 'Occasione speciale',
}

const formatGiftDelivery = (gift) => {
    const deliveryDate = new Date(gift?.delivery_at)
    if (Number.isNaN(deliveryDate.getTime())) return 'Data non disponibile'

    const options = {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }

    try {
        return new Intl.DateTimeFormat('it-IT', {
            ...options,
            timeZone: gift?.timezone || undefined,
        }).format(deliveryDate).replace(',', ' alle')
    }
    catch {
        return new Intl.DateTimeFormat('it-IT', options).format(deliveryDate).replace(',', ' alle')
    }
}


const Order = () => {

    const navigate = useNavigate()
    const { width } = useWindowDimensions()
    const context = useContext(MainContext)
    const { orderId } = useParams()
    const [order, setOrder] = useState(null)
    const [logs, setLogs] = useState(null)
    const [selectedLog, setSelectedLog] = useState(null)
    const [copiedField, setCopiedField] = useState(null)
    const copyTimerRef = useRef(null)
    useEffect(() => {
        setCopiedField(null)
        return () => clearTimeout(copyTimerRef.current)
    }, [orderId])

    const copyOrderIdentifier = async (field, value) => {
        try {
            await navigator.clipboard.writeText(String(value))
            clearTimeout(copyTimerRef.current)
            setCopiedField(field)
            copyTimerRef.current = setTimeout(() => setCopiedField(null), 2000)
        } catch (error) {
            context.setNotificationBanner({
                title: 'COPIA NON RIUSCITA',
                body: 'Non è stato possibile copiare il valore. Puoi selezionarlo e copiarlo manualmente.'
            })
        }
    }

    const [actionsOpen, setActionsOpen] = useState(false)
    const actionsRef = useRef(null)
    const actionsTriggerRef = useRef(null)

    useEffect(() => {
        if (!actionsOpen) return
        const closeOutside = (event) => {
            if (!actionsRef.current?.contains(event.target)) setActionsOpen(false)
        }
        const closeOnEscape = (event) => {
            if (event.key === 'Escape') {
                setActionsOpen(false)
                actionsTriggerRef.current?.focus()
            }
        }
        document.addEventListener('pointerdown', closeOutside)
        document.addEventListener('keydown', closeOnEscape)
        return () => {
            document.removeEventListener('pointerdown', closeOutside)
            document.removeEventListener('keydown', closeOnEscape)
        }
    }, [actionsOpen])

    const [editMode, setEditMode] = useState(false)
    const [refundMode, setRefundMode] = useState(false)
    const [refundingProducts, setRefundingProducts] = useState({})
    const [openAlert, setOpenAlert] = useState(false)
    const [refundStatus, setRefundStatus] = useState(DialogStatus.Default)
    const [installmentsOptions, setInstallmentsOptions] = useState({
        installments: 0,
        frequencyNumber: 1,
        frequencyUnit: "month",
        management: "stripe",
        startDate: new Date(new Date().setHours(23, 59, 0, 0))
    })

    const [confirmDialog, setConfirmDialog] = useState(false)

    const notesInputRef = useRef(null)
    const [note, setNote] = useState('')
    const [sendDisabled, setSendDisabled] = useState(true)

    const [error, setError] = useState(null)

    const [oldOrder, setOldOrder] = useState(null)
    const [confrimationEmailSent, setConfirmationEmailSent] = useState(false)

    const [deleteOrderDialog, setDeleteOrderDialog] = useState(false)
    const [installmentMailDialog, setInstallmentMailDialog] = useState(false)
    const [installmentPaymentLinkDialog, setInstallmentPaymentLinkDialog] = useState(false)
    const [selectedInstallmentForMail, setSelectedInstallmentForMail] = useState(null)
    const [installmentRefundDialog, setInstallmentRefundDialog] = useState({
        open: false,
        installment: null,
        amount: '',
        loading: false,
    })
    const [paidIntents, setPaidIntents] = useState([])
    const [paidIntentsLoading, setPaidIntentsLoading] = useState(false)
    const [selectedPaidIntent, setSelectedPaidIntent] = useState(null)
    const [associateIntentStatus, setAssociateIntentStatus] = useState(null)

    const [actionStatus, setActionStatus] = useState(null)
    const dialogActions = useMemo(() => {
        const deleteOrder = async () => {
            setActionStatus(ButtonStatus.Loading)
            try {
                await api.delete(`/admin/orders/${orderId}`)
                setActionStatus(ButtonStatus.Success)
                navigate(-1)
            }
            catch (e) {
                console.error(e)
                setActionStatus(ButtonStatus.Error)
            }
            setActionStatus(null)
        }

        return [{
            label: 'ELIMINA',
            status: actionStatus,
            accentColor: 'var(--sf-red)',
            confirmText: "ELIMINA",
            disabled: actionStatus === ButtonStatus.Loading,
            onClick: async () => {
                await deleteOrder()
            }
        }]
    }, [deleteOrderDialog, orderId, actionStatus])

    const orderStatusOptions = [
        {
            id: 0,
            name: "Completato",
            value: "succeeded"
        },
        {
            id: 1,
            name: "In attesa",
            value: "pending"
        },
        {
            id: 3,
            name: "Non Pagato",
            value: "unpaid"
        },
        {
            id: 4,
            name: "Pagato Parzialmente",
            value: "partially_paid"
        },
        {
            id: 5,
            name: "Richiede azione",
            value: "requires_action"
        },
        {
            id: 6,
            name: 'In attesa di rimborso',
            value: 'waiting_for_refund'
        },
        {
            id: 7,
            name: 'Rimborsato',
            value: 'refunded'
        },
        {
            id: 8,
            name: 'Rimborso fallito',
            value: 'refund_failed'
        },
        {
            id: 9,
            name: 'Parzialmente rimborsato',
            value: 'partially_refunded'
        },
        {
            id: 10,
            name: 'Abbandonato',
            value: 'abandoned'
        },
        {
            id: 11,
            name: 'Annullato',
            value: 'cancelled'
        }
    ]

    const installmentsManagementOptions = [
        {
            id: 0,
            name: "Manuale",
            value: "manual"
        },
        {
            id: 1,
            name: "Stripe",
            value: "stripe"
        },
    ]

    const installmentsTypeOptions = [
        {
            id: 0,
            name: "Interna",
            value: "internal"
        },
        {
            id: 1,
            name: "Esterna",
            value: "external"
        },
    ]

    const installmentsStatusOptions = [
        {
            id: 0,
            name: "In attesa",
            value: "pending"
        },
        {
            id: 1,
            name: "Pagato",
            value: "succeeded"
        },
        {
            id: 3,
            name: "Non Pagato",
            value: "unpaid"
        },
        {
            id: 4,
            name: "Rimborsato",
            value: "refunded"
        },
        {
            id: 5,
            name: "Parzialmente rimborsato",
            value: "partially_refunded"
        },
        {
            id: 6,
            name: "Non pagherà",
            value: "abandoned"
        }
    ]

    const paymentMethodIcon = useMemo(() => {
        if (!order) return;
        if (!order.payment_method) return;

        switch (order.payment_method) {
            case 'paypal':
                return <PayPal className={styles.paymentMethod} />
            case 'scalapay':
                return <Scalapay className={styles.paymentMethod} />
            case 'customer_balance':
                return <BankStripe className={styles.paymentMethod} />
            case 'visa':
                return <Visa className={styles.paymentMethod} />
            case 'mastercard':
                return <Mastercard className={styles.paymentMethod} />
            case 'amex':
                return <Amex className={styles.paymentMethod} />
            default:
                return <CreditCardIcon className={styles.paymentMethod} />
        }
    }, [order])

    const hasRefunds = useMemo(() => {
        if (!order) return
        return [
            OrderStatus.Refunded,
            OrderStatus.PartiallyRefunded,
            OrderStatus.WaitingForRefund,
        ].includes(order.status)
    }, [order])

    const totalRefundAmount = useMemo(() => {
        if (!order) return
        return order.products.map(
            p => p.refunds.filter(r => r.status === "succeeded")
                .map(r => r.amount)
                .reduce((a, c) => a += c, 0)
        ).reduce((a, c) => a += c, 0)
    }, [order])

    const installmentPaymentSummary = useMemo(() => {
        if (!order?.installments?.length) return null
        return getInstallmentsPaymentSummary(order, totalRefundAmount)
    }, [order, totalRefundAmount])

    const status = useMemo(() => {
        if (!order) {
            return null;
        }

        return getOrderStatusBadge(order.status)
    }, [order])

    const canBeRefunded = useMemo(() => {
        return [
            OrderStatus.Succeeded,
            OrderStatus.RefundFailed,
            OrderStatus.PartiallyRefunded,
            OrderStatus.PartiallyPaid
        ].includes(order?.status) && order?.session_id && order?.intent_id
    }, [order])

    const canBePaidInInstallments = useMemo(() => {
        return (
            order?.status === OrderStatus.PartiallyPaid ||
            order?.status === OrderStatus.Pending ||
            order?.status === OrderStatus.Failed ||
            order?.status === OrderStatus.Unpaid ||
            order?.status === OrderStatus.Refunded ||
            order?.status === OrderStatus.PartiallyRefunded || !order?.intent_id
        )
    }, [order])

    const canBeUpdated = useMemo(() => {
        if ([OrderStatus.Refunded, OrderStatus.PartiallyRefunded].includes(order?.status) && order?.installments?.length > 0) {
            return true
        }

        if ([OrderStatus.Refunded, OrderStatus.PartiallyRefunded, OrderStatus.WaitingForRefund].includes(order?.status) && order?.session_id && order?.intent_id) {
            return false
        }

        if (order?.status === OrderStatus.Succeeded && order?.session_id && order?.intent_id) {
            return false
        }

        if (order?.status === OrderStatus.Succeeded && !order?.session_id && !order?.intent_id) {
            return true
        }


        let totalRefunded = 0
        for (const product of order?.products ?? []) {
            const refundedAmount = product.refunds.filter(r => r.status === "succeeded")
                .map(r => r.amount)
                .reduce((a, c) => a += c, 0)
            totalRefunded += refundedAmount * 100
        }

        if (totalRefunded >= order?.amount && order?.amount > 0 && totalRefunded > 0) {
            return false
        }

        return true
    }, [order])

    const discounts = useMemo(() => {
        if (!order) return 0

        return order.products.filter(p => p.discounted_price != null).reduce((a, c) => {
            const { price, discounted_price } = c
            a += (discounted_price ?? 0) - price
            return a
        }, 0)
    }, [order])

    const orderCoupons = useMemo(() => {
        if (!order || order.metadata?.manual_pricing) return []

        const rawCoupons = [
            order.discount,
            order.coupon,
            ...(Array.isArray(order.coupons) ? order.coupons : []),
        ]
        const coupons = rawCoupons.map(normalizeOrderCoupon).filter(Boolean)

        return coupons.filter((coupon, index) => (
            coupons.findIndex((candidate) => (
                candidate.code === coupon.code
                && candidate.amountOff === coupon.amountOff
                && candidate.percentOff === coupon.percentOff
            )) === index
        ))
    }, [order])

    const stripeFees = useMemo(() => {
        if (!order) return null
        if (!order.session_id) return null
        const fixedFee = 0.25
        const percentageFee = 0.015
        if (order.installments) {
            let fees = 0
            for (const i of order.installments) {
                fees += i.amount / 100 * percentageFee + fixedFee
            }
            return fees
        }
        else {
            return order.amount / 100 * percentageFee + fixedFee
        }
    }, [order])

    const refundingProductsNumber = useMemo(() => {
        return Object.values(refundingProducts).filter(rp => rp.enabled).length
    }, [refundingProducts])

    const refundAmount = useMemo(() => {
        return Object.values(refundingProducts)
            .filter(rp => rp.enabled)
            .map(rp => rp.amount)
            .reduce((a, c) => a += c, 0)
    }, [refundingProducts])

    const installmentsAmountMismatch = useMemo(() => {
        return getInstallmentsAmountMismatch(order)
    }, [order])

    const installmentsAmountError = useMemo(() => {
        if (!installmentsAmountMismatch) return null

        const difference = Math.abs(installmentsAmountMismatch.difference) / 100
        const direction = installmentsAmountMismatch.difference > 0 ? 'supera' : 'non raggiunge'

        return `La somma delle rate (${formatPrice(installmentsAmountMismatch.installmentsTotal / 100)} €) ${direction} il totale dell'ordine (${formatPrice(installmentsAmountMismatch.orderAmount / 100)} €) di ${formatPrice(difference)} €.`
    }, [installmentsAmountMismatch])

    const isInstallmentsAmountValid = useMemo(() => {
        return !installmentsAmountMismatch
    }, [installmentsAmountMismatch])

    const canSaveInstallments = isInstallmentsAmountValid || hasUnchangedInstallmentAmounts(order, oldOrder)

    const canCheckPaidIntents = useMemo(() => {
        return (
            order?.status === OrderStatus.Pending &&
            !order?.intent_id &&
            (!order?.installments || order.installments.length === 0)
        )
    }, [order])

    const getOrder = useCallback(async () => {
        try {
            const order = await api.get(`/admin/orders/${orderId}`)
            setOrder(order)
            setOldOrder(structuredClone(order))
        }
        catch (e) {
            console.error(e)
        }
    }, [orderId])

    const [stripeErrors, setStripeErrors] = useState(null)
    const [checkingStripeErrors, setCheckingStripeErrors] = useState(false)
    const [stripeErrorsFailed, setStripeErrorsFailed] = useState(false)

    useEffect(() => {
        setStripeErrors(null)
        setStripeErrorsFailed(false)
    }, [orderId])

    const checkStripeErrors = async () => {
        setCheckingStripeErrors(true)
        setStripeErrorsFailed(false)
        try {
            setStripeErrors(await api.get(`/admin/orders/${orderId}/payment_errors`))
        } catch {
            setStripeErrorsFailed(true)
        } finally {
            setCheckingStripeErrors(false)
        }
    }

    const getOrderLogs = async () => {
        try {
            const logs = await api.get(`/admin/orders/${orderId}/logs`)
            setLogs(logs)
        }
        catch (e) {
            console.error(e)
        }
    }

    useEffect(() => {

        if (orderId) {
            getOrder()
            getOrderLogs()
        }
    }, [orderId, getOrder])

    const checkPaidPaymentIntents = useCallback(async () => {
        if (!order?.id || !canCheckPaidIntents) return

        setPaidIntentsLoading(true)
        try {
            const intents = await api.get(`/admin/orders/${order.id}/paid_payment_intents`)
            setPaidIntents(intents ?? [])
        }
        catch (e) {
            console.error(e)
            setPaidIntents([])
        }
        setPaidIntentsLoading(false)
    }, [order?.id, canCheckPaidIntents])

    useEffect(() => {
        if (canCheckPaidIntents) {
            checkPaidPaymentIntents()
        }
        else {
            setPaidIntents([])
            setSelectedPaidIntent(null)
        }
    }, [canCheckPaidIntents, checkPaidPaymentIntents])

    useEffect(() => {
        if (refundMode && order) {
            const refundingProducts = {}
            for (const product of order.products) {
                const refundedAmount = product.refunds.filter(r => r.status === "succeeded")
                    .map(r => r.amount)
                    .reduce((a, c) => a += c, 0)

                let refundableAmount = Math.round(((product.discounted_price ?? product.price) - refundedAmount) * 100) / 100
                if (order.installments?.find(i => i.status === OrderStatus.Succeeded)) {
                    refundableAmount = Math.min(
                        (order.installments?.map(i => i.status === OrderStatus.Succeeded ? i.amount : 0).reduce((a, c) => a += c, 0) ?? 0) / 100,
                        Math.round((product.discounted_price - refundedAmount) * 100) / 100)
                }
                refundingProducts[product.id] = {
                    amount: refundableAmount,
                    enabled: refundableAmount > 0,
                    canBeRefunded: refundableAmount > 0,
                    deleteSubscription: false,
                    product,
                    refundableAmount,
                }
            }
            setRefundingProducts(refundingProducts)
        } else {
            setRefundingProducts({})
        }
    }, [refundMode])

    useEffect(() => {
        if (editMode) {
            setRefundMode(false)
        }
    }, [editMode])

    useEffect(() => {
        if (note) {
            if (note.trim().length === 0) {
                setSendDisabled(true)
            }
            else {
                setSendDisabled(false)
            }
        }
    }, [note])

    const onChangeRefundAmount = (value, productId) => {
        const product = order.products.find(o => o.id === productId)
        setRefundingProducts(rp => {
            if ((product.discounted_price ?? product.price) < value * 100) {
                rp[productId].amount = (product.discounted_price ?? product.price)
            } else if (value < 0) {
                rp[productId].amount = 0
            } else {
                rp[productId].amount = value * 100
            }
            return { ...rp }
        })
    }

    const onDialogClose = () => {
        setOpenAlert(false)
        setRefundStatus(DialogStatus.Default)
    }

    const alertActions = useMemo(() => {
        const refund = async () => {
            setRefundStatus(DialogStatus.Loading)
            try {
                const product_refunds = Object.values(refundingProducts).filter(p => p.enabled).map(p => ({
                    product_id: p.product.id,
                    amount: p.amount,
                    cancel_subscription: p.deleteSubscription
                }))
                await api.post(`/admin/orders/${order.id}/refund`, product_refunds)
                setRefundStatus(DialogStatus.Success)
                setRefundMode(false)
                await getOrder()
                await getOrderLogs()
            } catch (e) {
                console.error(e)
                setRefundStatus(DialogStatus.Error)
            }
        }

        return [
            {
                label: refundStatus === DialogStatus.Success ? "OK" : "CONFERMA RIMBORSO",
                onClick: refundStatus === DialogStatus.Success ? onDialogClose : refund,
            }
        ]
    }, [refundStatus, order, refundingProducts])

    const onProductPriceChanged = (v, product_id) => {

        const orderAmount = order.amount

        setOrder((prev) => {

            prev.metadata = { ...prev.metadata, manual_pricing: true };

            // Aggiorna il prezzo scontato del prodotto
            prev.products = prev.products.map(p => {
                if (p.id === product_id) {
                    p.discounted_price = Math.round(Number(v) * 100);
                }
                return p;
            });


            // Calcola il nuovo totale dell'ordine
            const newAmount = parseInt(
                prev.products
                    .map(p => p.discounted_price ?? p.price)
                    .reduce((a, c) => a += c, 0)
            );


            // Aggiorna il totale dell'ordine
            prev.amount = newAmount;

            if (prev.installments && newAmount !== orderAmount) {
                let installments = [...prev.installments];

                // Filtra le rate non pagate
                const paidInstallments = installments.filter(isFixedInstallment);
                const unpaidInstallments = installments.filter(installment => !isFixedInstallment(installment));

                const totalPaid = getInstallmentsTotal(paidInstallments);

                if (unpaidInstallments.length > 0) {
                    const amountDiff = newAmount - totalPaid; // Differenza tra il nuovo totale e quello precedente
                    const remainder = amountDiff % unpaidInstallments.length; // Calcola la rimanenza
                    unpaidInstallments.forEach((installment, index) => {
                        if (amountDiff <= 0) {
                            installment.amount = 0
                        }
                        else {
                            installment.amount = Math.floor(amountDiff / unpaidInstallments.length); // Aggiungi la quota fissa
                            if (index === 0) {
                                installment.amount += remainder; // Aggiungi la rimanenza alla prima rata non pagata
                            }
                        }
                    });
                }
                prev.installments = installments;

                if (!isInstallmentsTotalMatchingOrder(prev)) {
                    setError("L'importo delle rate non coincide con il totale dell'ordine")
                }
                else {
                    setError(null)
                }
            }


            return { ...prev };
        });
    };

    const generateInstallments = (numInstallments, totalAmount, frequencyNumber, frequencyUnit, startDate = new Date()) => {
        const installmentAmount = Math.floor(totalAmount / numInstallments); // Arrotonda per difetto a intero
        const remainder = totalAmount - (installmentAmount * numInstallments); // Calcola la rimanenza
        const installments = [];

        for (let i = 0; i < numInstallments; i++) {
            let dueDate;

            switch (frequencyUnit) {
                case "day":
                    dueDate = addDays(startDate, frequencyNumber * (i + 1));
                    break;
                case "week":
                    dueDate = addWeeks(startDate, frequencyNumber * (i + 1));
                    break;
                case "month":
                    dueDate = addMonths(startDate, frequencyNumber * (i + 1));
                    break;
                case "year":
                    dueDate = addYears(startDate, frequencyNumber * (i + 1));
                    break;
                default:
                    throw new Error("Tipo di periodo non valido");
            }

            installments.push({
                amount: i === 0 ? installmentAmount + remainder : installmentAmount, // Aggiungi la rimanenza alla prima rata
                expired_at: dueDate.toISOString().split('.')[0],
                status: 'pending',
                type: InstallmentType.Internal
            });
        }

        return installments;
    };

    const generateOrderInstallments = () => {
        let installments = generateInstallments(installmentsOptions.installments, order?.amount, installmentsOptions.frequencyNumber, installmentsOptions.frequencyUnit, installmentsOptions.startDate)
        if (order.status === OrderStatus.PartiallyPaid && order.intent_id) {
            installments[0].status = OrderStatus.Succeeded
            let theoricAmount = installments[0].amount
            installments[0].amount = order.amount - order.payment_instructions.amount_remaining
            installments[0].intent_id = order.intent_id
            let remaining_amount = theoricAmount - installments[0].amount
            let remaining_installments = installments.slice(1).length
            if (remaining_installments > 0) {
                let amount_per_installment = remaining_amount / remaining_installments
                installments = installments.map((i, index) => {
                    if (index > 0) {
                        i.amount = Math.round((i.amount + amount_per_installment) * 100) / 100
                    }
                    return i
                })
            }

        }
        setOrder((prev) => {
            prev.installments = installments
            return { ...prev }
        })
    }

    const isRefundLocked = installment => isLockedRefundedInstallment(installment, oldOrder)

    const updateInstallmentAmount = (index, newAmount) => {
        const amount = Number(newAmount)
        if (!Number.isFinite(amount)) return
        setError(null)
        setOrder(prev => ({ ...prev, installments: updateInstallmentAmounts(
            prev.installments, index, Math.round(amount * 100), prev.amount, isRefundLocked) }))
    }

    const [updateLoading, setUpdateLoading] = useState(false)
    const updateOrder = async (force = false) => {
        setUpdateLoading(true)
        let body = {
            manual_pricing: order.metadata?.manual_pricing === true,
            amount: order.amount,
            status: order.status,
            completed_at: order.completed_at,
            test: order.test,
            products: order.products.map(p => ({
                id: p.id,
                price: p.price,
                discounted_price: p.discounted_price,
            })),
        }

        if (order.installments) {
            body.installments = order.installments.map(i => ({
                id: i.id ?? null,
                amount: i.amount,
                expired_at: i.expired_at,
                status: i.status,
                intent_id: i.intent_id ?? null,
                refunded_amount: i.refunded_amount,
                completed_at: i.completed_at,
                type: i.type
            }))
        }

        try {
            if (oldOrder.status === OrderStatus.Succeeded && order.status !== OrderStatus.Succeeded && !force) {
                setConfirmDialog(true)
            }
            else {
                await api.put(`/admin/orders/${order.id}`, body)
                setEditMode(false)
                await getOrder()
                await getOrderLogs()
            }
        }
        catch (e) {
            console.error(e)
        }
        setUpdateLoading(false)
    }

    const getInstallmentStatus = (status) => {
        switch (status) {
            case OrderStatus.Pending:
                return {
                    label: "In Attesa",
                    color: "var(--sf-gray)"
                }
            case OrderStatus.Processing:
                return {
                    label: "In elaborazione",
                    color: "var(--sf-yellow)"
                }
            case OrderStatus.Succeeded:
                return {
                    label: "Completato",
                    color: "var(--sf-light-green)"
                }
            case OrderStatus.Failed:
                return {
                    label: "Errore",
                    color: "var(--sf-red)"
                }
            case OrderStatus.Refunded:
                return {
                    label: "Rimborsato",
                    color: "var(--sf-blue)"
                }
            case OrderStatus.WaitingForRefund:
                return {
                    label: "In attesa del rimborso",
                    color: "var(--sf-gray)"
                }
            case OrderStatus.PartiallyRefunded:
                return {
                    label: "Parzialmente rimborsato",
                    color: "var(--sf-orange)"
                }
            case OrderStatus.RefundFailed:
                return {
                    label: "Rimborso fallito",
                    color: "var(--sf-red)"
                }
            case OrderStatus.Unpaid:
                return {
                    label: "Non pagato",
                    color: "var(--sf-red)"
                }
            case OrderStatus.PartiallyPaid:
                return {
                    label: "Parzialmente pagato",
                    color: "var(--sf-orange)"
                }
            case OrderStatus.Abandoned:
                return {
                    label: "Non pagherà",
                    color: "black"
                }
            default: return null
        }
    }

    const onInstallmentsStatusChanged = (index, value) => {

        setOrder((prev) => {
            if (isRefundLocked(prev.installments[index])) return prev

            const nextStatus = installmentsStatusOptions.find(f => f.value === value).value
            const installment = prev.installments[index]

            if (installment.status !== OrderStatus.Succeeded && nextStatus === OrderStatus.Succeeded) {
                installment.intent_id = null
                installment.completed_at = installment.completed_at ?? new Date().toISOString().split('.')[0]
            }
            installment.status = nextStatus
            if (!isRefundedInstallment(installment)) installment.refunded_amount = 0
            if (installment.status === OrderStatus.Refunded) {
                installment.refunded_amount = installment.amount
            }
            let installments = [...prev.installments];
            let installments_status = installments.map(i => i.status)
            if (installments_status.every(s => s === OrderStatus.Pending)) {
                prev.status = OrderStatus.Pending
            }
            if (installments_status.every(s => s === OrderStatus.Succeeded)) {
                prev.status = OrderStatus.Succeeded
                prev.completed_at = prev.completed_at ?? new Date().toISOString().split('.')[0]
            }
            if (installments_status.find(s => s === OrderStatus.Pending) && installments_status.find(s => s === OrderStatus.Succeeded)) {
                prev.status = OrderStatus.PartiallyPaid
            }
            if (installments_status.find(s => s === OrderStatus.Refunded) && !installments_status.find(s => s === OrderStatus.Succeeded)) {
                prev.status = OrderStatus.Refunded
            }
            if (installments_status.find(s => s === OrderStatus.PartiallyRefunded) && !installments_status.find(s => s === OrderStatus.Succeeded)) {
                prev.status = OrderStatus.PartiallyRefunded
            }
            return { ...prev }
        })
    }

    const onInstallmentAdd = () => {
        setOrder((prev) => {
            let installments = [...prev.installments];

            // Ottieni la data dell'ultima rata
            const lastInstallment = installments[installments.length - 1];
            let nextDate = lastInstallment
                ? new Date(lastInstallment.expired_at) // Usa la data di scadenza dell'ultima rata
                : new Date(new Date().setHours(23, 59, 0, 0)); // Se non ci sono rate, usa la data corrente

            // Calcola la prossima data in base alla frequenza
            switch (installmentsOptions.frequencyUnit) {
                case "day":
                    nextDate = addDays(nextDate, installmentsOptions.frequencyNumber);
                    break;
                case "week":
                    nextDate = addWeeks(nextDate, installmentsOptions.frequencyNumber);
                    break;
                case "month":
                    nextDate = addMonths(nextDate, installmentsOptions.frequencyNumber);
                    break;
                case "year":
                    nextDate = addYears(nextDate, installmentsOptions.frequencyNumber);
                    break;
                default:
                    throw new Error("Tipo di frequenza non valido");
            }

            // Aggiungi la nuova rata
            installments.push({
                amount: 0,
                expired_at: new Date(nextDate.toISOString().split('.')[0]), // Imposta la nuova data
                status: 'pending',
                type: InstallmentType.Internal
            });

            prev.installments = installments;
            return { ...prev };
        });
    };

    const onInstallmentDelete = (index) => {
        setOrder((prev) => {
            if (!prev.installments[index] || isFixedInstallment(prev.installments[index])) return prev

            let installments = prev.installments.map(installment => ({ ...installment }));

            // Rimuovi la rata corrispondente all'indice
            const deletedInstallment = installments.splice(index, 1)[0];

            // Calcola l'importo totale delle rate non pagate
            const unpaidInstallments = installments.filter(i => !isFixedInstallment(i));
            const unpaidTotal = unpaidInstallments.reduce((sum, i) => sum + i.amount, 0);

            // Aggiungi l'importo della rata eliminata alle rate non pagate
            const totalToRedistribute = unpaidTotal + getInstallmentsTotal([deletedInstallment]);

            // Redistribuisci l'importo sulle rate non pagate
            const installmentAmount = unpaidInstallments.length ? Math.floor(totalToRedistribute / unpaidInstallments.length) : 0;
            const remainder = totalToRedistribute - (installmentAmount * unpaidInstallments.length);

            unpaidInstallments.forEach((installment, i) => {
                installment.amount = i === 0 ? installmentAmount + remainder : installmentAmount; // Aggiungi il resto alla prima rata
            });

            return { ...prev, installments };
        });
    };

    const onInstallmentExpirationChanged = (index, value) => {
        setOrder((prev) => {
            if (isRefundLocked(prev.installments[index])) return prev

            prev.installments[index].expired_at = value
            return { ...prev }
        })
    }

    const onInstallmentPaymentDateChanged = (index, value) => {
        setOrder((prev) => {
            if (isRefundLocked(prev.installments[index])) return prev

            prev.installments[index].completed_at = value
            return { ...prev }
        })
    }

    const onInstallmentTypeChanged = (index, value) => {
        setOrder((prev) => {
            if (isRefundLocked(prev.installments[index])) return prev

            prev.installments[index].type = value
            return { ...prev }
        })
    }

    const handleNoteChange = (event) => {
        if (event.target.value.trim().length === 0) {
            setSendDisabled(true)
        }
        else {
            setSendDisabled(false)
        }
        setNote(event.target.value);
    }

    const addOrderNote = async () => {
        if (sendDisabled || !note.trim()) return
        setSendDisabled(true)
        try {
            await api.post(`/admin/orders/${order.id}/notes`, {
                note: note
            })
            setNote('')
            await getOrder()
            await getOrderLogs()
        }
        catch (e) {
            console.error(e)
        }
        setSendDisabled(false)
    }

    const sendConfirmationEmail = async () => {
        try {
            await api.post(`/admin/orders/${order.id}/confirmation_email`)
            context.setNotificationBanner({
                title: 'Mail Inviata',
                body: 'Email di conferma inviata con successo!'
            })
            setConfirmationEmailSent(true)
        }
        catch (e) {
            console.error(e)
        }
    }

    const associatePaidPaymentIntent = async () => {
        if (!selectedPaidIntent) return

        setAssociateIntentStatus(ButtonStatus.Loading)
        try {
            const updatedOrder = await api.post(`/admin/orders/${order.id}/paid_payment_intents/${selectedPaidIntent.id}/associate`)
            setOrder(updatedOrder)
            setOldOrder(structuredClone(updatedOrder))
            setPaidIntents([])
            setSelectedPaidIntent(null)
            setAssociateIntentStatus(ButtonStatus.Success)
            context.setNotificationBanner({
                title: 'ORDINE COMPLETATO',
                body: "Payment intent associato correttamente all'ordine."
            })
            await getOrderLogs()
        }
        catch (e) {
            console.error(e)
            setAssociateIntentStatus(ButtonStatus.Error)
        }
    }

    const getInstallmentPaymentMethodIcon = (installment) => {
        if (!installment) return;
        if (!installment.payment_method) {
            return
        }

        switch (installment.payment_method) {
            case 'paypal':
                return <PayPal className={styles.paymentMethod} />
            case 'scalapay':
                return <Scalapay className={styles.paymentMethod} />
            case 'customer_balance':
                return <BankStripe className={styles.paymentMethod} />
            case 'visa':
                return <Visa className={styles.paymentMethod} />
            case 'mastercard':
                return <Mastercard className={styles.paymentMethod} />
            case 'amex':
                return <Amex className={styles.paymentMethod} />
            default:
                return <CreditCardIcon className={styles.paymentMethod} />
        }
    }

    const sendOrderInstallmentEmail = async () => {
        try {
            await api.post(`/admin/orders/${order.id}/installment_expired_email`)
            context.setNotificationBanner({
                title: 'INIVATA!',
                body: 'Email di notifica inviata con successo!'
            })
            setInstallmentMailDialog(false)
        }
        catch (e) {
            console.error(e)
        }
    }

    const sendInstallmentPaymentLinkEmail = async (installmentId) => {
        try {
            await api.post(`/admin/orders/${order.id}/installments/${installmentId}/payment_link_email`)
            context.setNotificationBanner({
                title: 'INVIATA!',
                body: 'Email con link di pagamento inviata con successo!'
            })
        }
        catch (e) {
            console.error(e)
        }
    }

    const getInstallmentMaxRefundable = (installment) => {
        if (!installment) return 0
        const alreadyRefunded = installment.refunded_amount ?? 0
        return Math.max(0, installment.amount - alreadyRefunded)
    }

    const installmentRefundMaximum = getInstallmentMaxRefundable(installmentRefundDialog.installment)
    const installmentRefundCents = parseRefundAmount(installmentRefundDialog.amount)
    const installmentRefundValid = installmentRefundCents !== null && installmentRefundCents <= installmentRefundMaximum
    const installmentRefundViaStripe = Boolean(installmentRefundDialog.installment?.intent_id) && installmentRefundDialog.useStripe === true

    const openInstallmentRefundDialog = (installment) => {
        const maxRefundable = getInstallmentMaxRefundable(installment)

        if (maxRefundable <= 0) {
            context.setNotificationBanner({
                title: 'RIMBORSO NON DISPONIBILE',
                body: 'La rata risulta gia totalmente rimborsata.'
            })
            return
        }

        setInstallmentRefundDialog({
            open: true,
            installment,
            amount: (maxRefundable / 100).toFixed(2),
            useStripe: Boolean(installment.intent_id),
            loading: false,
        })
    }

    const closeInstallmentRefundDialog = () => {
        setInstallmentRefundDialog({
            open: false,
            installment: null,
            amount: '',
            loading: false,
        })
    }

    const submitInstallmentRefund = async () => {
        if (installmentRefundDialog.loading) return
        const installment = installmentRefundDialog.installment
        if (!installment) return

        const maxRefundable = getInstallmentMaxRefundable(installment)
        const amountInCents = parseRefundAmount(installmentRefundDialog.amount)

        if (amountInCents === null || amountInCents > maxRefundable) {
            context.setNotificationBanner({
                title: 'IMPORTO NON VALIDO',
                body: `Inserisci un valore tra 0,01 e ${(maxRefundable / 100).toFixed(2)}.`
            })
            return
        }

        const useStripe = installmentRefundViaStripe

        setInstallmentRefundDialog((prev) => ({ ...prev, loading: true }))
        try {
            await api.post(`/admin/orders/${order.id}/installments/${installment.id}/refund`, {
                amount: amountInCents,
                use_stripe: useStripe
            })

            context.setNotificationBanner({
                title: 'RIMBORSO CREATO',
                body: 'Rimborso rata registrato correttamente.'
            })

            closeInstallmentRefundDialog()
            await getOrder()
            await getOrderLogs()
        }
        catch (e) {
            console.error(e)
            context.setNotificationBanner({
                title: 'RIMBORSO NON RIUSCITO',
                body: 'Non è stato possibile completare il rimborso. Verifica lo stato del pagamento prima di riprovare.'
            })
            setInstallmentRefundDialog((prev) => ({ ...prev, loading: false }))
        }
    }

    return (
        <HeaderFooterLayout>
            <div className={styles.container}>
                {order &&
                    <div className={styles.section}>
                        <div className={styles.sectionInner}>
                            <Breadcrumb items={[
                                { label: 'Ordini', to: '/orders' },
                                { label: `Ordine #${order.order_no}` },
                            ]} />
                        </div>
                        <div className={styles.sectionInner}>
                            <div className={styles.orderWorkspace}>
                                <div className={styles.orderMain}>
                            {order.metadata?.gift &&
                                <Card>
                                    <h2 className={styles.orderEyebrow}>Regalo</h2>
                                    <div className={styles.giftInfo}>
                                        <div>
                                            <span>Destinatario</span>
                                            <b>{order.metadata.gift.recipient_name} {order.metadata.gift.recipient_surname}</b>
                                        </div>
                                        <div>
                                            <span>Email destinatario</span>
                                            <b>{order.metadata.gift.recipient_email}</b>
                                        </div>
                                        <div>
                                            <span>Invio programmato</span>
                                            <b>{formatGiftDelivery(order.metadata.gift)}</b>
                                        </div>
                                        <div>
                                            <span>Timezone</span>
                                            <b>{order.metadata.gift.timezone || 'UTC'}</b>
                                        </div>
                                        <div>
                                            <span>Occasione</span>
                                            <b>{GIFT_TEMPLATE_LABELS[order.metadata.gift.template] || order.metadata.gift.template || '—'}</b>
                                        </div>
                                        {order.metadata.gift.message &&
                                            <div className={styles.giftMessage}>
                                                <span>Messaggio</span>
                                                <b>“{order.metadata.gift.message}”</b>
                                            </div>
                                        }
                                    </div>
                                </Card>
                            }
                            <Card>
                                <div className={styles.orderCardHeader}>
                                    <div className={styles.orderIdentity}>
                                        <h1 className={styles.orderEyebrow}>Dettaglio ordine</h1>
                                    </div>
                                <div className={styles.headerActions}>
                                    {editMode && <Button appearance="subtle" disabled={updateLoading} onClick={() => {
                                        setOrder(structuredClone(oldOrder))
                                        setEditMode(false)
                                        setError(null)
                                    }}>ANNULLA MODIFICHE</Button>}
                                    {refundMode && <Button appearance="subtle" onClick={() => setRefundMode(false)}>ANNULLA RIMBORSO</Button>}
                                    <div className={styles.actionsMenu} ref={actionsRef} onBlur={(event) => {
                                        if (!event.currentTarget.contains(event.relatedTarget)) setActionsOpen(false)
                                    }}>
                                        <Button ref={actionsTriggerRef} appearance="subtle" additionalClass={styles.actionsTrigger}
                                            aria-label="Azioni ordine" aria-expanded={actionsOpen} aria-controls="order-actions"
                                            onClick={() => setActionsOpen(open => !open)}>
                                            <MenuIcon aria-hidden="true" />
                                        </Button>
                                        {actionsOpen && <div id="order-actions" role="group" aria-label="Azioni ordine" className={styles.actionsDropdown}>
                                            {canBeUpdated && <Button appearance="text" disabled={editMode || updateLoading} onClick={() => {
                                                setActionsOpen(false)
                                                setRefundMode(false)
                                                setEditMode(true)
                                                actionsTriggerRef.current?.focus()
                                            }}>Modifica</Button>}
                                            <Button appearance="text" disabled={!canBeRefunded || editMode || refundMode} onClick={() => {
                                                setActionsOpen(false)
                                                setRefundMode(true)
                                                actionsTriggerRef.current?.focus()
                                            }}>Rimborsa</Button>
                                            {(order.status === OrderStatus.Succeeded || order.installments?.length > 0) && !editMode &&
                                                <Button appearance="text" disabled={confrimationEmailSent} onClick={() => {
                                                    setActionsOpen(false)
                                                    actionsTriggerRef.current?.focus()
                                                    sendConfirmationEmail()
                                                }}>Email di conferma</Button>
                                            }
                                            <Button appearance="text" accentColor="var(--sf-red)" disabled={updateLoading} onClick={() => {
                                                setActionsOpen(false)
                                                setDeleteOrderDialog(true)
                                                actionsTriggerRef.current?.focus()
                                            }}>Elimina</Button>
                                        </div>}
                                    </div>
                                </div>
                                </div>
                                {editMode && <div className={styles.editNotice} role="status">Modalità modifica · Le modifiche saranno applicate al salvataggio.</div>}
                                <div className={styles.orderMetadata} aria-label="Informazioni ordine">
                                    {[
                                        { key: 'id', label: 'ID ordine', value: order.id },
                                        { key: 'order_no', label: 'Numero ordine', value: order.order_no },
                                    ].map(field => (
                                        <div className={styles.identifierField} key={field.key}>
                                            <span className={styles.identifierLabel}>{field.label}</span>
                                            <div className={styles.identifierValue}>
                                                <code>{field.value ?? '—'}</code>
                                                <Button appearance="subtle" requiresEdit={false}
                                                    additionalClass={styles.copyIdentifier}
                                                    disabled={field.value == null}
                                                    aria-label={`Copia ${field.label.toLowerCase()}`}
                                                    title={`Copia ${field.label.toLowerCase()}`}
                                                    onClick={() => copyOrderIdentifier(field.key, field.value)}>
                                                    {copiedField === field.key ? <CheckIcon aria-hidden="true" /> : <CopyIcon aria-hidden="true" />}
                                                    <span className={styles.copyFeedback} aria-live="polite">{copiedField === field.key ? 'Copiato' : 'Copia'}</span>
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                        <div className={styles.metadataStatus}>
                                            <span>Stato</span>
                                    {(!editMode || order.installments) &&
                                        <StatusBadge appearance="soft" {...status} />
                                    }
                                    {editMode && !order.installments &&
                                        <Select
                                            style={{ width: '100%', maxWidth: '260px', fontSize: '0.875rem' }}
                                            options={orderStatusOptions}
                                            selected={orderStatusOptions.find((o) => o.value === order.status)}
                                            onSelect={(value) => {
                                                setOrder({ ...order, status: value.value })
                                            }} />
                                    }

                                        </div>

                                    <div><span>Creato il</span><time dateTime={order.created_at}>{formatDateV2(order.created_at, { day: 'numeric', month: 'numeric', year: 'numeric' })} · {formatTimeV2(order.created_at)}</time></div>
                                    {order.updated_at && <div><span>Ultima modifica</span><time dateTime={order.updated_at}>{formatDateV2(order.updated_at, { day: 'numeric', month: 'numeric', year: 'numeric' })} · {formatTimeV2(order.updated_at)}</time></div>}
                                    {order.completed_at && !editMode && <div><span>Completato il</span><time dateTime={order.completed_at}>{formatDateV2(order.completed_at, { day: 'numeric', month: 'numeric', year: 'numeric' })} · {formatTimeV2(order.completed_at)}</time></div>}
                                    {order.status === 'succeeded' && editMode &&
                                        <div className={styles.metadataEditor}>
                                            <div className={typo.caption}><b>Completato il:</b>
                                            </div>
                                            <TextInput
                                                type="date"
                                                value={order.completed_at ? new Date(order.completed_at + 'Z') : new Date()}
                                                style={{ border: '1px solid rgba(var(--text-color-rgb), 12%)', borderRadius: '1rem', padding: '0.5rem' }}
                                                defaultValue={order.completed_at ? new Date(order.completed_at + 'Z') : new Date()}
                                                placeholder=""
                                                onKeyUp={(d) => {
                                                    setOrder({ ...order, completed_at: d.toISOString().split('.')[0] })
                                                }}
                                            />
                                        </div>
                                    }
                                    {order.intent_id && <div className={styles.metadataIntent}><span>Payment Intent Stripe</span><code>{order.intent_id}</code></div>}
                                    {paymentMethodIcon && <div><span>Metodo di pagamento</span>{paymentMethodIcon}</div>}
                                </div>
                                {canCheckPaidIntents && paidIntents.length > 0 &&
                                    <div className={styles.paymentIntentCheck}>
                                        <div className={styles.paymentIntentCheckHeader}>
                                            <div>
                                                <div className={styles.cardSectionTitle} style={{ paddingTop: 0 }}>PAYMENT INTENT PAGATI</div>
                                                <div className={typo.caption}>
                                                    Check su Stripe per payment intent succeeded associati alla mail <b>{order.user?.email}</b>.
                                                </div>
                                            </div>
                                            <Button
                                                appearance="text"
                                                status={paidIntentsLoading ? ButtonStatus.Loading : null}
                                                onClick={checkPaidPaymentIntents}
                                            >
                                                RICONTROLLA
                                            </Button>
                                        </div>
                                        <table className={styles.paymentIntentTable}>
                                            <thead>
                                                <tr>
                                                    <th>ID</th>
                                                    <th>Importo</th>
                                                    <th>Pagato il</th>
                                                    <th>Metodo</th>
                                                    <th>Descrizione</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <Each of={paidIntents} render={(intent) => (
                                                    <tr>
                                                        <td>{intent.id}</td>
                                                        <td>
                                                            {formatPrice(intent.amount / 100)} {intent.currency?.toUpperCase()}
                                                            {intent.amount !== order.amount &&
                                                                <div className={styles.amountWarning}>diverso dal totale ordine</div>
                                                            }
                                                        </td>
                                                        <td>{intent.paid_at ? `${formatDateV2(intent.paid_at, { day: 'numeric', month: 'numeric', year: 'numeric' })}, ${formatTimeV2(intent.paid_at)}` : '--'}</td>
                                                        <td>{intent.payment_method ?? '--'}</td>
                                                        <td>{intent.description || '--'}</td>
                                                        <td>
                                                            <Button appearance="text" onClick={() => setSelectedPaidIntent(intent)}>
                                                                ASSOCIA
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                )} />
                                            </tbody>
                                        </table>
                                    </div>
                                }
                                {(editMode || order.metadata?.manual_pricing) &&
                                    <div style={{ margin: '1rem 0' }}>
                                        <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center' }}>
                                            <Checkbox label="Prezzi manuali del backoffice" labelPosition="end" value={order.metadata?.manual_pricing === true} disabled={!editMode}
                                                onChange={value => setOrder(prev => ({ ...prev,
                                                    metadata: { ...prev.metadata, manual_pricing: value },
                                                    amount: value ? prev.products.reduce((sum, p) => sum + (p.discounted_price ?? p.price), 0) : prev.amount,
                                                }))} />
                                        </div>
                                        <small>Gli importi dei prodotti restano fissi e sostituiscono eventuali coupon. Disattiva per usare il listino aggiornato alla riapertura dell’ordine.</small>
                                    </div>
                                }
                                <div className={styles.tableScroll} role="region" aria-label="Prodotti dell’ordine" tabIndex={0}>
                                <table className={styles.productsTable}>
                                    <thead>
                                        <tr>
                                            {refundMode &&
                                                <th></th>
                                            }
                                            <th>N.</th>
                                            <th>Prodotto</th>
                                            <th>Prezzo</th>
                                            <th>Sconto</th>
                                            <th>Totale</th>
                                            {hasRefunds &&
                                                <th>Rimborso</th>
                                            }
                                            {refundMode &&
                                                <th>Rimborsa</th>
                                            }
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <Each of={order.products} render={(product, index) => {
                                            return (
                                                <tr>
                                                    {refundMode &&
                                                        <td data-label="Seleziona">
                                                            <Checkbox
                                                                size={"1.5rem"}
                                                                value={refundingProducts[product.id]?.enabled}
                                                                disabled={!refundingProducts[product.id]?.canBeRefunded}
                                                                onChange={v => setRefundingProducts(rf => {
                                                                    rf[product.id].enabled = v
                                                                    rf[product.id].deleteSubscription = v && rf[product.id].deleteSubscription
                                                                    return { ...rf }
                                                                })}
                                                            />
                                                        </td>
                                                    }
                                                    <td data-label="Prodotto n.">{index + 1}</td>
                                                    <td data-label="Prodotto" className={styles.productName}>{product.name}</td>
                                                    <td data-label="Prezzo">{formatPrice(product.price / 100)} €</td>
                                                    <td data-label="Sconto">{product.discounted_price !== null && product.price !== 0 ? Math.round((product.price - product.discounted_price) / product.price * 100) : '--'}%</td>
                                                    <td data-label="Totale">
                                                        <>
                                                            {!editMode &&
                                                                <>{formatPrice(product.discounted_price !== null ? product.discounted_price / 100 : product.price / 100)} €</>
                                                            }
                                                            {editMode &&
                                                                <TextInput
                                                                    onKeyUp={(v) => onProductPriceChanged(v, product.id)}
                                                                    type="number"
                                                                    numberType="float"
                                                                    value={(product.discounted_price ?? product.price) / 100}
                                                                    min={order.installments ? (
                                                                        order.installments.map(i => i.status === "succeeded" ? i.amount : 0).reduce((a, c) => a += c, 0) / 100 - order.products.map(p => p.id !== product.id ? p.discounted_price : 0).reduce((a, c) => a += c, 0)
                                                                    ) : 0}
                                                                    style={{
                                                                        padding: ".5rem 0.5rem",
                                                                        maxWidth: "96px",
                                                                        borderRadius: '8px'
                                                                    }}
                                                                />
                                                            }
                                                        </>
                                                    </td>
                                                    {hasRefunds &&
                                                        <td data-label="Rimborso">
                                                            {formatPrice(product.refunds
                                                                .filter(r => ["succeeded", "waiting_for_refund"].includes(r.status))
                                                                .map(r => r.amount)
                                                                .reduce((a, c) => a += c, 0)) !== '00,00' &&

                                                                `${formatPrice(-product.refunds
                                                                    .filter(r => r.status === "succeeded")
                                                                    .map(r => r.amount)
                                                                    .reduce((a, c) => a += c, 0) / 100)
                                                                } €`
                                                            }
                                                        </td>
                                                    }
                                                    {refundMode &&
                                                        <td data-label="Importo rimborso">
                                                            <TextInput
                                                                onKeyUp={(v) => onChangeRefundAmount(v, product.id)}
                                                                type="number"
                                                                numberType="float"
                                                                value={refundingProducts[product.id]?.amount ? refundingProducts[product.id].amount / 100 : 0}
                                                                min={0}
                                                                max={refundingProducts[product.id]?.refundableAmount ? refundingProducts[product.id].refundableAmount / 100 : 0}
                                                                style={{
                                                                    padding: ".5rem 0.5rem",
                                                                    maxWidth: "96px",
                                                                    borderRadius: '8px'
                                                                }}
                                                            />
                                                        </td>
                                                    }
                                                </tr>
                                            )
                                        }} />

                                    </tbody>
                                </table>
                                </div>
                                <div className={styles.financialSummary} aria-label="Riepilogo economico">
                                    <dl className={styles.priceBreakdown}>
                                        <div><dt>Subtotale</dt><dd>{formatPrice(order.products.reduce((sum, p) => sum + p.price, 0) / 100)} €</dd></div>
                                        {discounts < 0 && <div><dt>Sconti</dt><dd>{formatPrice(-discounts / 100)} €</dd></div>}
                                        {orderCoupons.map((coupon, index) => <div key={`${coupon.code}-${index}`}>
                                            <dt>Coupon {coupon.code}</dt><dd>
                                                {coupon.amountOff !== null ? `−${formatPrice(Number(coupon.amountOff) / 100)} €` : coupon.percentOff !== null ? `−${coupon.percentOff}%` : '—'}
                                            </dd>
                                        </div>)}
                                        {!installmentPaymentSummary && totalRefundAmount > 0 && <div><dt>Rimborsato</dt><dd>−{formatPrice(totalRefundAmount / 100)} €</dd></div>}
                                        <div className={styles.orderTotal}><dt>Totale ordine</dt><dd>{formatPrice((order.amount - (installmentPaymentSummary ? 0 : totalRefundAmount || 0)) / 100)} €</dd></div>
                                    </dl>
                                    {installmentPaymentSummary && <div className={styles.paymentSummary}>
                                        <div className={styles.paymentMetric}><span>Pagato netto</span><strong>{formatPrice(installmentPaymentSummary.paid / 100)} €</strong></div>
                                        <div className={`${styles.paymentMetric} ${styles.balanceMetric}`}><span>Da pagare</span><strong>{formatPrice(installmentPaymentSummary.remaining / 100)} €</strong></div>
                                        {totalRefundAmount > 0 && <p className={styles.refundExplanation}>Pagamenti rimborsati: <b>{formatPrice(totalRefundAmount / 100)} €</b></p>}
                                    </div>}
                                </div>

                                {order.installments && order.installments.length > 0 && !editMode &&
                                    <>
                                        <div className={styles.cardSectionTitle}>
                                            RATE
                                        </div>
                                        <div className={styles.tableScroll} role="region" aria-label="Rate dell’ordine" tabIndex={0}>
                                        <table className={styles.installmentsTable}>
                                            <thead>
                                                <tr className={styles.tableHeaders}>
                                                    <th>N.</th>
                                                    <th>Importo</th>
                                                    <th>Stato</th>
                                                    <th>Tipo</th>
                                                    <th>Scadenza</th>
                                                    {order.installments.some(i => i.completed_at) &&
                                                        <th>Pagata il</th>
                                                    }
                                                    {order.installments.find(i => [OrderStatus.Refunded, OrderStatus.PartiallyRefunded].includes(i.status)) &&
                                                        <th>Rimborso</th>
                                                    }
                                                    {order.installments.find(i => i.intent_id && i.completed_at) &&
                                                        <th>ID Stripe</th>
                                                    }
                                                    {order.installments.find(i => i.intent_id && i.completed_at && i.payment_method) &&
                                                        <th>Metodo</th>
                                                    }
                                                    <th>Azioni</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <Each of={order.installments} render={(installment, index) => {

                                                    let badge = getInstallmentStatus(installment.status)
                                                    if (addDays(new Date(), 5) >= new Date(installment.expired_at) && ![OrderStatus.Succeeded, OrderStatus.Refunded, OrderStatus.PartiallyRefunded, OrderStatus.WaitingForRefund, OrderStatus.RefundFailed, OrderStatus.Abandoned].includes(installment.status)) {
                                                        badge = getInstallmentStatus(OrderStatus.PartiallyPaid)
                                                        badge.label = 'In scadenza'
                                                    }
                                                    if (new Date(installment.expired_at) < new Date() && ![OrderStatus.Succeeded, OrderStatus.Refunded, OrderStatus.PartiallyRefunded, OrderStatus.WaitingForRefund, OrderStatus.RefundFailed, OrderStatus.Abandoned].includes(installment.status)) {
                                                        badge = getInstallmentStatus(OrderStatus.Unpaid)
                                                        badge.label = 'Scaduta'
                                                    }
                                                    if (new Date(installment.expired_at) < new Date() && installment.status === OrderStatus.Abandoned) {
                                                        badge = getInstallmentStatus(OrderStatus.Abandoned)
                                                        badge.label = 'Non pagherà'
                                                    }

                                                    return (
                                                        <tr className={installment.status === OrderStatus.Refunded ? styles.refundedRow : undefined}>
                                                            <td data-label="Rata">#{index + 1}</td>
                                                            <td data-label="Importo" className={styles.installmentAmount}>{formatPrice(installment.amount / 100)} €</td>
                                                            <td data-label="Stato"><StatusBadge appearance="soft" {...badge} /></td>
                                                            <td data-label="Tipo">{installmentsTypeOptions.find(o => o.value === installment.type)?.name ?? "—"}</td>
                                                            <td data-label="Scadenza">{formatDateV2(installment.expired_at, { day: 'numeric', month: 'numeric', year: 'numeric' })}, {formatTimeV2(installment.expired_at)}</td>
                                                            {order.installments.some(i => i.completed_at) &&
                                                                <>
                                                                    {installment.completed_at ? <td data-label="Pagata il">{formatDateV2(installment.completed_at, { day: 'numeric', month: 'numeric', year: 'numeric' })}, {formatTimeV2(installment.completed_at)}</td> : <td data-label="Pagata il">—</td>}
                                                                </>
                                                            }
                                                            {order.installments.find(i => [OrderStatus.Refunded, OrderStatus.PartiallyRefunded].includes(i.status)) &&
                                                                <td data-label="Rimborso">{formatPrice((installment.refunded_amount ?? 0) / 100)} €</td>
                                                            }
                                                            {order.installments.some(i => i.intent_id && i.completed_at) &&
                                                                <td data-label="ID Stripe" className={styles.intentId}>{installment.intent_id && installment.completed_at ? installment.intent_id : "—"}</td>
                                                            }
                                                            {order.installments.some(i => i.intent_id && i.completed_at && i.payment_method) &&
                                                                <td data-label="Metodo">{installment.intent_id && installment.completed_at && installment.payment_method ? getInstallmentPaymentMethodIcon(installment) : "—"}</td>
                                                            }
                                                            <td data-label="Azioni" className={styles.rateActions}>
                                                                {![OrderStatus.Succeeded, OrderStatus.Refunded, OrderStatus.PartiallyRefunded].includes(installment.status) &&
                                                                    <Button appearance="subtle" onClick={() => {
                                                                        setSelectedInstallmentForMail(installment)
                                                                        setInstallmentPaymentLinkDialog(true)
                                                                    }}>
                                                                        INVIA LINK PAGAMENTO
                                                                    </Button>
                                                                }
                                                                {[OrderStatus.Succeeded, OrderStatus.PartiallyRefunded, OrderStatus.RefundFailed].includes(installment.status) &&
                                                                    <Button appearance="subtle" onClick={() => {
                                                                        openInstallmentRefundDialog(installment)
                                                                    }}>
                                                                        RIMBORSA RATA
                                                                    </Button>
                                                                }
                                                            </td>
                                                        </tr>
                                                    )
                                                }} />
                                            </tbody>
                                        </table>
                                        </div>
                                        <div className={styles.installmentEmail}>
                                            {order.installments.find(i => i.expired_at && new Date(i.expired_at) < new Date() && ![OrderStatus.Succeeded, OrderStatus.Refunded, OrderStatus.PartiallyRefunded, OrderStatus.Abandoned].includes(i.status)) &&
                                                <Button appearance="text" onClick={() => {
                                                    setInstallmentMailDialog(true)
                                                }}>SOLLECITA PAGAMENTO</Button>
                                            }
                                        </div>
                                    </>
                                }
                                {refundMode &&
                                    <>
                                        <div className={styles.cardSectionTitle}>RIMBORSO</div>
                                        <div className={styles.refundInfo}>
                                            <div className={styles.refundLabel}>Rimborsa <b>{refundingProductsNumber}</b> prodotti per un totale di <b>{formatPrice(refundAmount / 100)} €</b></div>
                                            <Button disabled={Object.values(refundingProducts).filter(p => p.enabled)?.length === 0 || refundAmount < 0.5}
                                                onClick={() => {
                                                    setOpenAlert(true)
                                                }}>
                                                CONFERMA
                                            </Button>
                                        </div>
                                    </>
                                }
                                {editMode && canBePaidInInstallments &&
                                    <>
                                        <div className={styles.cardSectionTitle}>
                                            RATEIZZAZIONE
                                            <div className={styles.spacer}></div>
                                            {editMode && order.installments &&
                                                <>
                                                    <CreateButton onClick={onInstallmentAdd}>
                                                         Aggiungi rata
                                                    </CreateButton>
                                                    <Button appearance="subtle" onClick={() => {
                                                        if (oldOrder.installments) {
                                                            setOrder((prev) => {
                                                                prev.installments = [...prev.installments.filter(i => i.intent_id || isRefundedInstallment(i))]
                                                                return { ...prev }
                                                            })
                                                        }
                                                        else {
                                                            setOrder((prev) => {
                                                                prev.installments = oldOrder.installments ? structuredClone(oldOrder.installments.filter(i => i.intent_id || isRefundedInstallment(i))) : null
                                                                return { ...prev }
                                                            })
                                                        }
                                                    }}>
                                                        {oldOrder.installments ? "RESETTA" : "ANNULLA"}
                                                    </Button>
                                                </>
                                            }
                                        </div>
                                        {!order.installments &&
                                            <div className={styles.installments}>
                                                <div className={styles.installmentsOptions}>
                                                    <div className={typo.caption} style={{ paddingLeft: '0.5rem', opacity: 0.5, fontWeight: 700 }}>Rate</div>
                                                    <TextInput type={"number"} value={installmentsOptions.installments} style={{ minWidth: '90px' }} onKeyUp={(value) => {
                                                        setInstallmentsOptions({ ...installmentsOptions, installments: parseInt(value) })
                                                    }} />
                                                </div>
                                                <div className={styles.installmentsOptions}>
                                                    <div className={typo.caption} style={{ paddingLeft: '0.5rem', opacity: 0.5, fontWeight: 700 }}>Frequenza</div>
                                                    <FrequencySelector onChange={(frequencyNumber, frequencyUnit) => {
                                                        setInstallmentsOptions({ ...installmentsOptions, frequencyNumber, frequencyUnit })
                                                    }} />
                                                </div>
                                                <div className={styles.installmentsOptions}>
                                                    <div className={typo.caption} style={{ paddingLeft: '0.5rem', opacity: 0.5, fontWeight: 700 }}>Data di inizio</div>
                                                    <TextInput
                                                        type="date"
                                                        value={installmentsOptions.startDate}
                                                        style={{ border: '1px solid rgba(var(--text-color-rgb), 12%)', borderRadius: '1rem', padding: '0.5rem' }}
                                                        // defaultValue={new Date(new Date().setHours(23, 59, 0, 0))}
                                                        placeholder=""
                                                        onKeyUp={(d) => {
                                                            setInstallmentsOptions({ ...installmentsOptions, startDate: new Date(d) })
                                                        }}
                                                    />
                                                </div>
                                                <div className={styles.installmentsOptions}>
                                                    <div className={typo.caption} style={{ paddingLeft: '0.5rem', opacity: 0, fontWeight: 700, }}>GENERA</div>
                                                    <Button style={{ padding: '0.7rem 2rem', fontSize: '1rem' }} disabled={installmentsOptions.installments < 2} onClick={generateOrderInstallments}>GENERA</Button>
                                                </div>
                                            </div>
                                        }

                                        {order.installments?.length > 0 && <div className={styles.planSummary} aria-label="Riepilogo piano rate">
                                            <div><span>Totale ordine</span><strong>{formatPrice(order.amount / 100)} €</strong></div>
                                            <div><span>Totale rate attive</span><strong>{formatPrice(getInstallmentsTotal(order.installments) / 100)} €</strong></div>
                                            <div><span>Verifica importi</span><strong className={isInstallmentsAmountValid ? styles.planBalanced : styles.planUnbalanced}>
                                                {isInstallmentsAmountValid ? 'Importi coerenti' : `Differenza: ${formatPrice((getInstallmentsTotal(order.installments) - order.amount) / 100)} €`}
                                            </strong></div>
                                        </div>}
                                        <p className={styles.planHelp}>Modificando un importo, il residuo viene distribuito sulle rate successive modificabili. Le rate rimborsate restano nello storico.</p>
                                        <div className={styles.orderInstallments}>
                                            <Each of={order.installments} render={(installment, index) => {
                                                const refunded = isRefundLocked(installment)
                                                const paidViaStripe = Boolean(installment.intent_id) && installment.status === OrderStatus.Succeeded
                                                const dueDate = installment.expired_at instanceof Date ? installment.expired_at
                                                    : new Date(/(?:Z|[+-]\d{2}:\d{2})$/.test(installment.expired_at) ? installment.expired_at : `${installment.expired_at}Z`)
                                                return (
                                                    <section className={`${styles.rateEditor} ${refunded ? styles.rateEditorLocked : ''}`} aria-labelledby={`rate-title-${index}`}>
                                                        <div className={styles.rateEditorHeader}>
                                                            <div className={styles.rateEditorTitle}>
                                                                <h3 id={`rate-title-${index}`}>Rata {index + 1}</h3>
                                                                <StatusBadge appearance="soft" {...getInstallmentStatus(installment.status)} />
                                                                {!installment.id && <span className={styles.newRate}>Nuova</span>}
                                                            </div>
                                                            {refunded ? <span className={styles.rateReadOnly}>Sola lettura</span> : !isFixedInstallment(installment) &&
                                                                <Button appearance="subtle" requiresEdit additionalClass={styles.removeRate}
                                                                    aria-label={`Elimina rata ${index + 1}`} title="Elimina rata"
                                                                    onClick={() => onInstallmentDelete(index)}><DeleteIcon aria-hidden="true" /></Button>}
                                                        </div>
                                                        {refunded ? <dl className={styles.refundedRateDetails}>
                                                            <div><dt>Importo</dt><dd>{formatPrice(installment.amount / 100)} €</dd></div>
                                                            <div><dt>Rimborsato</dt><dd>{formatPrice((installment.refunded_amount ?? 0) / 100)} €</dd></div>
                                                            <div><dt>Scadenza</dt><dd>{formatDateV2(installment.expired_at, { day: 'numeric', month: 'numeric', year: 'numeric' })} · {formatTimeV2(installment.expired_at)}</dd></div>
                                                            <div><dt>Tipo</dt><dd>{installmentsTypeOptions.find(option => option.value === installment.type)?.name ?? '—'}</dd></div>
                                                        </dl> : <div className={styles.rateEditorFields}>
                                                            <div className={styles.rateField}>
                                                                <label className={styles.rateAmountLabel}>
                                                                    <span>Importo (€)</span>
                                                                    <TextInput disabled={paidViaStripe} type="number" numberType="float"
                                                                        value={parseFloat(installment.amount / 100)}
                                                                        style={{ width: '100%', maxWidth: 'none', padding: '.7rem .8rem', borderRadius: '8px', fontSize: '1rem' }}
                                                                        onKeyUp={value => {
                                                                            const amount = parseFloat(value)
                                                                            if (!isNaN(amount)) updateInstallmentAmount(index, amount)
                                                                        }} />
                                                                </label>
                                                            </div>
                                                            <div className={`${styles.rateField} ${styles.rateDateField}`} role="group" aria-labelledby={`rate-due-${index}`}>
                                                                <span id={`rate-due-${index}`}>Scadenza</span>
                                                                <TextInput disabled={paidViaStripe} type="date" value={dueDate} defaultValue={dueDate}
                                                                    style={{ width: '100%', border: '1px solid rgba(var(--text-color-rgb), 12%)', borderRadius: '8px', padding: '.65rem' }}
                                                                    onKeyUp={date => { if (date) onInstallmentExpirationChanged(index, date.toISOString().split('.')[0]) }} />
                                                            </div>
                                                            <div className={styles.rateField}>
                                                                <label htmlFor={`rate-status-${index}`}>Stato</label>
                                                                <select id={`rate-status-${index}`} disabled={paidViaStripe} value={installment.status}
                                                                    onChange={event => onInstallmentsStatusChanged(index, event.target.value)}>
                                                                    {installmentsStatusOptions.map(option => <option key={option.value} value={option.value}>{option.name}</option>)}
                                                                </select>
                                                            </div>
                                                            <div className={styles.rateField}>
                                                                <label htmlFor={`rate-type-${index}`}>Tipo</label>
                                                                <select id={`rate-type-${index}`} disabled={paidViaStripe} value={installment.type}
                                                                    onChange={event => onInstallmentTypeChanged(index, event.target.value)}>
                                                                    {installmentsTypeOptions.map(option => <option key={option.value} value={option.value}>{option.name}</option>)}
                                                                </select>
                                                            </div>
                                                            {installment.status === OrderStatus.Succeeded && <div className={`${styles.rateField} ${styles.rateDateField}`} role="group" aria-labelledby={`rate-paid-${index}`}>
                                                                <span id={`rate-paid-${index}`}>Data di pagamento</span>
                                                                <TextInput type="date" max={new Date()}
                                                                    value={installment.completed_at ? new Date(installment.completed_at.endsWith('Z') ? installment.completed_at : `${installment.completed_at}Z`) : new Date()}
                                                                    style={{ width: '100%', border: '1px solid rgba(var(--text-color-rgb), 12%)', borderRadius: '8px', padding: '.65rem' }}
                                                                    onKeyUp={date => { if (date) onInstallmentPaymentDateChanged(index, date.toISOString().split('.')[0]) }} />
                                                            </div>}
                                                        </div>}
                                                        {(refunded || paidViaStripe || installment.intent_id) && <div className={styles.rateEditorFooter}>
                                                            {refunded && <span>Rimborso registrato: questa rata non è modificabile.</span>}
                                                            {paidViaStripe && <span>Pagamento Stripe completato: importo, scadenza, stato e tipo sono bloccati.</span>}
                                                            {installment.intent_id && <code title="Payment Intent Stripe">{installment.intent_id}</code>}
                                                        </div>}
                                                    </section>
                                                )
                                            }} />
                                        </div>
                                    </>
                                }
                                {editMode &&
                                    <div className={styles.installmentsActions}>
                                        <span className={styles.saveHint}>Controlla gli importi e salva le modifiche.</span>
                                        {(error || installmentsAmountError) &&
                                            <div role="alert" className={typo.caption} style={{ color: 'var(--sf-red)' }}>
                                                {error || (canSaveInstallments
                                                    ? "Le rate attive non coprono il totale: puoi salvare gli stati perché gli importi del piano non sono cambiati."
                                                    : installmentsAmountError)}
                                            </div>
                                        }
                                        <Button
                                            disabled={
                                                updateLoading || !canSaveInstallments
                                            }
                                            status={updateLoading ? ButtonStatus.Loading : null}
                                            onClick={async () => {
                                                await updateOrder()
                                            }}>SALVA MODIFICHE</Button>
                                    </div>
                                }
                            </Card>
                                </div>
                                <aside className={styles.orderSidebar} aria-label="Cliente e attività dell’ordine" tabIndex={0}>
                            <Card>
                                    <h2 className={styles.orderEyebrow}>Cliente</h2>
                                <div className={styles.userCard}>
                                    <div className={styles.user}>
                                        <img src={order.user?.picture ?? UserPlaceholder} alt="avatar" className={styles.userPicture} />
                                        <div className={styles.userInfo}>
                                            <a href={`/users/students/${order.user?.id}`}><div className={styles.userName}>{order.user?.name} {order.user?.surname}</div></a>
                                            <a className={styles.userEmail} href={`mailto:${order.user?.email}`}>{order.user?.email}</a>
                                            <a className={styles.userPhone} href={`tel:${order.user?.phone}`}>{order.user?.phone}</a>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                                <Card>
                                    <h2 className={styles.orderEyebrow}>Note</h2>
                                    <div className={styles.notes}>
                                        {!order.notes?.length && <p className={styles.emptyState}>Nessuna nota. Aggiungi informazioni utili per chi gestisce l’ordine.</p>}
                                        <Each of={order.notes ?? []} render={(note, index) => {
                                            return (
                                                <div className={styles.note}>
                                                    <div className={styles.noteHeader}>
                                                        <div className={styles.noteAdmin}>
                                                            <img className={styles.noteAdminPicture} src={note.created_by?.picture} alt="admin-avatar" />
                                                            <div className={styles.noteAdminName}>{note.created_by?.name} {note.created_by?.surname}</div>
                                                        </div>
                                                        <div className={typo.caption}>
                                                            {formatDateV2(note.created_at, { day: 'numeric', month: 'numeric', year: 'numeric' })}, {formatTimeV2(note.created_at)}
                                                        </div>
                                                    </div>
                                                    <div className={styles.noteContent}>
                                                        {note.note}
                                                    </div>
                                                </div>
                                            )
                                        }} />
                                        <div className={styles.noteInput}>
                                            <img src={context.user?.picture ?? UserPlaceholder} className={styles.noteInputPicture} alt="" />
                                            <TextareaAutosize
                                                style={{ height: 'unset' }}
                                                ref={notesInputRef}
                                                value={note}
                                                minRows={1}
                                                maxRows={note !== '' ? 5 : 1}
                                                type="text"
                                                className={styles.noteInputEditor}
                                                aria-label="Nuova nota"
                                                placeholder="aggiungi una nota..."
                                                onChange={handleNoteChange}
                                                onKeyDown={handleNoteChange} />
                                            <Button appearance="subtle" aria-label="Aggiungi nota" requiresEdit disabled={sendDisabled || !note.trim()} onClick={addOrderNote}><SendIcon className={styles.sendIcon} /></Button>
                                        </div>
                                    </div>
                                </Card>
                                <Card>
                                    <h2 className={styles.orderEyebrow}>Errori</h2>
                                    <Button appearance="subtle" disabled={checkingStripeErrors} onClick={checkStripeErrors}>
                                        {checkingStripeErrors ? 'Verifica in corso…' : 'Verifica su Stripe'}
                                    </Button>
                                    <div aria-live="polite">
                                        {(stripeErrorsFailed || stripeErrors?.unavailable) && <p className={styles.emptyState}>Impossibile completare la verifica su Stripe. Riprova tra poco.</p>}
                                        {stripeErrors && !stripeErrors.unavailable && !stripeErrors.errors.length && <p className={styles.emptyState}>
                                            {stripeErrors.linked ? 'Stripe non restituisce un errore per i pagamenti collegati. Lo storico potrebbe non essere più disponibile.' : 'Nessun pagamento Stripe collegato a questo ordine.'}
                                        </p>}
                                        {stripeErrors?.errors.map((paymentError) => <div className={styles.error} key={paymentError.intent_id}>
                                            <div className={styles.errorHeader}>Stripe{paymentError.installment_id ? ` · Rata #${paymentError.installment_id}` : ''}</div>
                                            <div className={styles.errorContent}>{paymentError.message}</div>
                                            {paymentError.code && <div className={typo.caption}>Codice: {paymentError.code}</div>}
                                            <div className={typo.caption}>{paymentError.intent_id}</div>
                                        </div>)}
                                    </div>
                                    <div className={styles.errors}>
                                        {!order.errors?.length && <p className={styles.emptyState}>Nessun errore registrato per questo ordine.</p>}
                                        <Each of={order.errors ?? []} render={(error, index) => {
                                            return (
                                                <div className={styles.error}>
                                                    <div className={styles.errorHeader}>
                                                        <div className={typo.caption} style={{ fontWeight: 700 }}>
                                                            {formatDateV2(error.created_at, { day: 'numeric', month: 'numeric', year: 'numeric' })}, {formatTimeV2(error.created_at)}
                                                        </div>
                                                    </div>
                                                    <div className={styles.errorContent}>
                                                        {error.message}
                                                    </div>
                                                </div>
                                            )
                                        }} />
                                    </div>
                                </Card>
                                <Card>
                                    <h2 className={styles.orderEyebrow}>Eventi</h2>
                                    {!logs?.length && <p className={styles.emptyState}>Nessun evento registrato per questo ordine.</p>}
                                    <div className={styles.logs}>
                                        <div className={styles.logsList}>
                                            <Each of={Object.keys(groupLogsByDate(logs ?? [])).sort((a, b) => new Date(b + 'T00:00:00') - new Date(a + 'T00:00:00'))} render={(key) => {
                                                let d = (new Date(key)).toISOString()
                                                return (
                                                    <>
                                                        <div className={styles.logGroupDate}>{formatDateV2(d, { day: 'numeric', month: 'short', year: 'numeric' }, 'it', true, false)}</div>
                                                        <Each of={groupLogsByDate(logs ?? [])[key].sort((a, b) => {
                                                            return new Date(b.created_at) - new Date(a.created_at)
                                                        })} render={(log) => {
                                                            const { name, verb, obj, predicate, color } = getLogPhrase(log)
                                                            return (
                                                                <button type="button" aria-pressed={selectedLog?.id === log.id} className={`${styles.log} ${selectedLog?.id === log.id ? styles.selected : ''}`} onClick={() => { setSelectedLog(log) }}>
                                                                    <div className={styles.logPhrase}>
                                                                        {name} <b style={{ color: color }}>{verb}</b> {obj} {predicate ? `(${predicate})` : ''}
                                                                    </div>
                                                                    <div className={styles.logTime}>
                                                                        {formatTimeV2(log.created_at, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                                                    </div>
                                                                </button>
                                                            )
                                                        }} />
                                                    </>
                                                )
                                            }} />
                                        </div>
                                        {selectedLog &&
                                            <div className={styles.logDetail}>
                                                <Button appearance="text" additionalClass={styles.closeLog} onClick={() => setSelectedLog(null)}>Chiudi dettaglio</Button>
                                                <JsonView src={selectedLog} dark={true} theme="atom" enableClipboard={false} />
                                            </div>
                                        }
                                    </div>
                                </Card>
                                </aside>
                            </div>
                        </div>
                    </div>
                }
                <AlertDialog
                    open={openAlert}
                    title={"Rimborsa prodotti"}
                    html={
                        (
                            <div className={styles.refundRecap}>
                                Sei sicuro di voler rimborsare i seguenti prodotti?
                                <table style={{ width: '100%', marginTop: '1rem' }}>
                                    <thead>
                                        <tr>
                                            <th>N.</th>
                                            <th>Prodotto</th>
                                            <th>Prezzo</th>
                                            <th>Rimborso</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <Each
                                            of={Object.values(refundingProducts).filter(p => p.enabled)}
                                            render={(refundProduct, index) => (
                                                <tr>
                                                    <td>{index + 1}.</td>
                                                    <td>{refundProduct.product.name}</td>
                                                    <td>{formatPrice(refundProduct.product.discounted_price / 100)}</td>
                                                    <td>{formatPrice(refundProduct.amount / 100)}</td>
                                                </tr>
                                            )}
                                        />
                                    </tbody>
                                </table>
                            </div>
                        )
                    }
                    status={refundStatus}
                    onClose={onDialogClose}
                    actions={alertActions}
                />
                <AlertDialog
                    open={confirmDialog}
                    title="Modificare l'ordine?"
                    text="Sei sicuro di voler modificare un ordine completato?"
                    actions={[
                        {
                            label: "Conferma",
                            onClick: async () => {
                                setConfirmDialog(false)
                                await updateOrder(true)
                            }
                        }]}
                />
                <AlertDialog open={installmentMailDialog}
                    title="Sollecita pagamento rate scadute"
                    text={`Sei sicuro di voler inviare una email di sollecito pagamento per le rate scadute dell'ordine **#${order?.order_no}**?`}
                    onClose={() => setInstallmentMailDialog(false)}
                    actions={[
                        {
                            label: "ANNULLA",
                            accentColor: "var(--sf-red)",
                            onClick: () => {
                                setInstallmentMailDialog(false)
                            }
                        },
                        {
                            label: "INVIA EMAIL",
                            onClick: async () => {
                                try {
                                    await sendOrderInstallmentEmail()
                                    setInstallmentMailDialog(false)
                                } catch (error) {
                                    console.error(error)
                                }
                            }
                        }]}
                />
                <AlertDialog
                    open={installmentPaymentLinkDialog}
                    title="Inviare link pagamento rata"
                    text={`Vuoi inviare una email con link di pagamento per la rata ${selectedInstallmentForMail ? `#${order?.installments?.findIndex(i => i.id === selectedInstallmentForMail.id) + 1}` : ''} dell'ordine **#${order?.order_no}**?`}
                    onClose={() => {
                        setInstallmentPaymentLinkDialog(false)
                        setSelectedInstallmentForMail(null)
                    }}
                    actions={[
                        {
                            label: "ANNULLA",
                            accentColor: "var(--sf-red)",
                            onClick: () => {
                                setInstallmentPaymentLinkDialog(false)
                                setSelectedInstallmentForMail(null)
                            }
                        },
                        {
                            label: "INVIA EMAIL",
                            onClick: async () => {
                                if (!selectedInstallmentForMail) return
                                await sendInstallmentPaymentLinkEmail(selectedInstallmentForMail.id)
                                setInstallmentPaymentLinkDialog(false)
                                setSelectedInstallmentForMail(null)
                            }
                        }
                    ]}
                />
                <AlertDialog
                    open={!!selectedPaidIntent}
                    title="Associare Payment Intent?"
                    html={
                        selectedPaidIntent ? (
                            <div className={styles.paymentIntentDialog}>
                                <div>Vuoi associare questo pagamento all'ordine <b>#{order?.order_no}</b>?</div>
                                <div className={styles.paymentIntentDialogRows}>
                                    <div><b>ID Stripe:</b> {selectedPaidIntent.id}</div>
                                    <div><b>Importo:</b> {formatPrice(selectedPaidIntent.amount / 100)} {selectedPaidIntent.currency?.toUpperCase()}</div>
                                    <div><b>Pagato il:</b> {selectedPaidIntent.paid_at ? `${formatDateV2(selectedPaidIntent.paid_at, { day: 'numeric', month: 'numeric', year: 'numeric' })}, ${formatTimeV2(selectedPaidIntent.paid_at)}` : '--'}</div>
                                    <div><b>Metodo:</b> {selectedPaidIntent.payment_method ?? '--'}</div>
                                    {selectedPaidIntent.description &&
                                        <div><b>Descrizione:</b> {selectedPaidIntent.description}</div>
                                    }
                                </div>
                                {selectedPaidIntent.amount !== order?.amount &&
                                    <div className={styles.paymentIntentDialogWarning}>
                                        L'importo del payment intent è diverso dal totale dell'ordine.
                                    </div>
                                }
                            </div>
                        ) : null
                    }
                    onClose={() => {
                        setSelectedPaidIntent(null)
                        setAssociateIntentStatus(null)
                    }}
                    actions={[
                        {
                            label: "ANNULLA",
                            accentColor: "var(--sf-red)",
                            onClick: () => {
                                setSelectedPaidIntent(null)
                                setAssociateIntentStatus(null)
                            }
                        },
                        {
                            label: "ASSOCIA",
                            status: associateIntentStatus,
                            disabled: associateIntentStatus === ButtonStatus.Loading,
                            onClick: associatePaidPaymentIntent
                        }
                    ]}
                />
                <AlertDialog
                    open={deleteOrderDialog}
                    title="Eliminare l'ordine?"
                    text={`Confermi di voler eliminare l'ordine **#${order?.order_no}**? <br/><br/>Eliminando l'ordine non rimuoverai eventuali iscrizioni dell'utente per i corsi acquistati.<br/><br/><span style="color: var(--sf-red)">L'azione non può essere annullata.</span>`}
                    onClose={() => setDeleteOrderDialog(false)}
                    actions={dialogActions}
                />
                <Dialog
                    open={installmentRefundDialog.open}
                    title="Rimborso rata"
                    onClose={() => { if (!installmentRefundDialog.loading) closeInstallmentRefundDialog() }}
                    style={{ width: 'min(520px, calc(100vw - 24px))', maxHeight: '90dvh' }}
                    contentMaxHeight="60dvh"
                    overflow="auto"
                    action={
                        <div className={styles.refundDialogActions}>
                            <Button appearance="subtle" disabled={installmentRefundDialog.loading} onClick={closeInstallmentRefundDialog}>Annulla</Button>
                            <Button disabled={installmentRefundDialog.loading || !installmentRefundValid}
                                status={installmentRefundDialog.loading ? ButtonStatus.Loading : null} onClick={submitInstallmentRefund}>
                                {installmentRefundDialog.loading ? 'Operazione in corso…' : installmentRefundViaStripe
                                    ? `Rimborsa${installmentRefundValid ? ` ${formatPrice(installmentRefundCents / 100)} €` : ''}`
                                    : 'Registra rimborso'}
                            </Button>
                        </div>
                    }
                >
                    <div className={styles.refundDialogBody}>
                        <div className={styles.refundContext}>
                            <strong>Rata #{installmentRefundDialog.installment ? order?.installments?.findIndex(i => i.id === installmentRefundDialog.installment.id) + 1 : '—'}</strong>
                            <span>Ordine #{order?.order_no}</span>
                        </div>
                        <dl className={styles.refundAmounts}>
                            <div><dt>Importo della rata</dt><dd>{formatPrice((installmentRefundDialog.installment?.amount ?? 0) / 100)} €</dd></div>
                            {(installmentRefundDialog.installment?.refunded_amount ?? 0) > 0 && <div><dt>Già rimborsato</dt><dd>{formatPrice(installmentRefundDialog.installment.refunded_amount / 100)} €</dd></div>}
                            <div className={styles.refundAvailable}><dt>Disponibile per il rimborso</dt><dd>{formatPrice(installmentRefundMaximum / 100)} €</dd></div>
                        </dl>
                        <div className={styles.refundRoute}>
                            {installmentRefundDialog.installment?.intent_id && <fieldset className={styles.refundMethod} disabled={installmentRefundDialog.loading}>
                                <legend>Modalità di rimborso</legend>
                                <label><input type="radio" name="installment-refund-method" checked={installmentRefundViaStripe}
                                    onChange={() => setInstallmentRefundDialog(prev => ({ ...prev, useStripe: true }))} /> Tramite Stripe</label>
                                <label><input type="radio" name="installment-refund-method" checked={!installmentRefundViaStripe}
                                    onChange={() => setInstallmentRefundDialog(prev => ({ ...prev, useStripe: false }))} /> Manuale (eseguito esternamente)</label>
                            </fieldset>}
                            <strong>{installmentRefundViaStripe ? 'Rimborso tramite Stripe' : 'Registrazione manuale'}</strong>
                            <p>{installmentRefundViaStripe
                                ? 'La richiesta sarà inviata a Stripe sul pagamento originale della rata.'
                                : 'Verrà registrato il rimborso nel backoffice. Questa operazione non trasferisce denaro.'}</p>
                        </div>
                        <div className={styles.refundAmountField}>
                            <div className={styles.refundAmountLabel}>
                                <label htmlFor="installment-refund-amount">Importo da rimborsare</label>
                                <Button appearance="text" requiresEdit={false} disabled={installmentRefundDialog.loading}
                                    onClick={() => setInstallmentRefundDialog(prev => ({ ...prev, amount: (installmentRefundMaximum / 100).toFixed(2) }))}>Usa massimo</Button>
                            </div>
                            <div className={`${styles.refundInput} ${!installmentRefundValid ? styles.refundInputInvalid : ''}`}>
                                <span aria-hidden="true">€</span>
                                <input id="installment-refund-amount" type="text" inputMode="decimal" autoComplete="off"
                                    value={installmentRefundDialog.amount} disabled={installmentRefundDialog.loading}
                                    aria-invalid={!installmentRefundValid} aria-describedby="installment-refund-help"
                                    onChange={event => setInstallmentRefundDialog(prev => ({ ...prev, amount: event.target.value }))} />
                            </div>
                            <p id="installment-refund-help" className={!installmentRefundValid ? styles.refundValidationError : styles.refundHelp} aria-live="polite">
                                {installmentRefundValid ? 'Puoi rimborsare tutto l’importo disponibile o solo una parte.'
                                    : `Inserisci un importo tra 0,01 € e ${formatPrice(installmentRefundMaximum / 100)} €, con massimo due decimali.`}
                            </p>
                        </div>
                    </div>
                </Dialog>
            </div>

        </HeaderFooterLayout>
    )

}

export default Order
